import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, OfficialStyle } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useToast } from "@/components/ui/Toast";
import ConfirmModal from "@/components/ui/ConfirmModal";
import {
  Plus,
  Search,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Loader2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
} from "lucide-react";

const statusLabels: Record<OfficialStyle["status"], { label: string; color: string }> = {
  DRAFT: { label: "草稿", color: "bg-yellow-100 text-yellow-700" },
  REVIEW: { label: "審核中", color: "bg-blue-100 text-blue-700" },
  PUBLISHED: { label: "已發布", color: "bg-green-100 text-green-700" },
  ARCHIVED: { label: "已封存", color: "bg-ceramic-100 text-ceramic-600" },
};

const statusFilters = [
  { value: "", label: "全部" },
  { value: "PUBLISHED", label: "已發布" },
  { value: "DRAFT", label: "草稿" },
  { value: "REVIEW", label: "審核中" },
  { value: "ARCHIVED", label: "已封存" },
];

export default function StyleListPage() {
  const [search, setSearch] = useState("");
  // Debounced search value that actually drives the query (avoids a request per keystroke).
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [, setOpenMenuId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<OfficialStyle | null>(null);
  const [confirmBatchRegen, setConfirmBatchRegen] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  // Batch selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Track which styles are currently regenerating their preview image
  const [regeneratingIds, setRegeneratingIds] = useState<Set<string>>(new Set());

  // Debounce the search input; reset to page 1 whenever the term changes.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const isSearchPending = search !== debouncedSearch;

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["styles", { page, status: statusFilter, search: debouncedSearch }],
    queryFn: () =>
      api.getStyles({
        page,
        pageSize: 20,
        status: statusFilter || undefined,
        search: debouncedSearch || undefined,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteStyle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["styles"] });
      toast.success("已刪除風格");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "刪除失敗，請重試");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      api.updateStyle(id, { isEnabled }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["styles"] });
      toast.success(variables.isEnabled ? "已顯示此風格" : "已隱藏此風格");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "更新失敗，請重試");
    },
  });

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id);
    setPendingDelete(null);
    setOpenMenuId(null);
  };

  const handleToggle = (id: string, currentEnabled: boolean) => {
    toggleMutation.mutate({ id, isEnabled: !currentEnabled });
    setOpenMenuId(null);
  };

  // Regenerate a single style's preview image
  const regeneratePreview = useCallback(async (style: OfficialStyle) => {
    if (!style.prompt || !style.name) return;

    setRegeneratingIds((prev) => new Set(prev).add(style.id));

    try {
      const result = await api.generateShowcaseImage({
        stylePrompt: style.prompt,
        styleName: style.name,
        oldImageUrl: style.referenceImages[0] || undefined,
      });

      if (result.success && result.data) {
        const newImages = [...style.referenceImages];
        newImages[0] = result.data.url;
        await api.updateStyle(style.id, { referenceImages: newImages });
        queryClient.invalidateQueries({ queryKey: ["styles"] });
      }
    } catch (error) {
      console.error("Regenerate failed:", error);
      toast.error(`「${style.name}」預覽圖生成失敗`);
    } finally {
      setRegeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(style.id);
        return next;
      });
    }
  }, [queryClient, toast]);

  // Batch regenerate (runs after confirmation)
  const runBatchRegenerate = useCallback(async () => {
    if (selectedIds.size === 0) return;

    const styles = data?.data.filter((s) => selectedIds.has(s.id)) ?? [];

    // Mark all as regenerating
    setRegeneratingIds((prev) => {
      const next = new Set(prev);
      styles.forEach((s) => next.add(s.id));
      return next;
    });

    // Clear selection
    setSelectedIds(new Set());

    // Regenerate one by one to avoid rate limiting
    for (const style of styles) {
      await regeneratePreview(style);
    }
  }, [selectedIds, data, regeneratePreview]);

  // Toggle selection
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select all on current page
  const toggleSelectAll = () => {
    if (!data?.data) return;
    const allOnPage = data.data.map((s) => s.id);
    const allSelected = allOnPage.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allOnPage));
    }
  };

  const hasSelection = selectedIds.size > 0;
  const allOnPageSelected = data?.data && data.data.length > 0 && data.data.every((s) => selectedIds.has(s.id));

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-ceramic-900">官方模板庫</h1>
          <p className="text-ceramic-500 mt-1">管理所有官方風格模板</p>
        </div>
        <div className="flex items-center gap-3">
          {hasSelection && (
            <button
              onClick={() => setConfirmBatchRegen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              批量重新生成預覽圖 ({selectedIds.size})
            </button>
          )}
          <Link
            to="/styles/new"
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
          >
            <Plus className="w-4 h-4" />
            新增模板
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex gap-2">
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => {
                setStatusFilter(filter.value);
                setPage(1);
              }}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                statusFilter === filter.value
                  ? "bg-ceramic-900 text-white"
                  : "bg-white border border-ceramic-200 text-ceramic-600 hover:border-ceramic-300"
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ceramic-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋風格..."
            aria-label="搜尋風格"
            className="pl-10 pr-10 py-2 w-64 border border-ceramic-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
          />
          {(isSearchPending || (isFetching && !!debouncedSearch)) ? (
            <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-ceramic-400 animate-spin" />
          ) : (
            search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="清除搜尋"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-ceramic-400 hover:bg-ceramic-100 hover:text-ceramic-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-ceramic-200 rounded-xl">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-ceramic-400" />
          </div>
        ) : error ? (
          <div className="text-center py-20 text-red-500">載入失敗，請重試</div>
        ) : data?.data.length === 0 ? (
          debouncedSearch || statusFilter ? (
            <div className="text-center py-20 text-ceramic-500">
              {debouncedSearch
                ? `找不到符合「${debouncedSearch}」的風格`
                : "沒有符合篩選條件的風格"}
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("");
                  setPage(1);
                }}
                className="block mx-auto mt-3 text-ceramic-900 font-medium hover:underline"
              >
                清除搜尋與篩選
              </button>
            </div>
          ) : (
            <div className="text-center py-20 text-ceramic-500">
              尚無風格資料
              <Link to="/styles/new" className="block mt-2 text-ceramic-900 font-medium hover:underline">
                立即新增第一個風格
              </Link>
            </div>
          )
        ) : (
          <table className="w-full">
            <thead className="bg-ceramic-50 border-b border-ceramic-200">
              <tr>
                <th className="px-4 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={!!allOnPageSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-ceramic-300 accent-ceramic-900"
                  />
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-ceramic-600">預覽</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-ceramic-600">名稱</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-ceramic-600">分類</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-ceramic-600">狀態</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-ceramic-600">使用次數</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-ceramic-600">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ceramic-100">
              {data?.data.map((style) => {
                const isRegenerating = regeneratingIds.has(style.id);
                const isToggling =
                  toggleMutation.isPending && toggleMutation.variables?.id === style.id;
                const isDeleting =
                  deleteMutation.isPending && deleteMutation.variables === style.id;
                return (
                  <tr key={style.id} className={cn("hover:bg-ceramic-50 transition-colors", selectedIds.has(style.id) && "bg-ceramic-50")}>
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(style.id)}
                        onChange={() => toggleSelect(style.id)}
                        className="w-4 h-4 rounded border-ceramic-300 accent-ceramic-900"
                      />
                    </td>
                    <td className="px-6 py-4">
                      {isRegenerating ? (
                        <div className="h-16 aspect-video rounded-lg border border-ceramic-200 bg-ceramic-50 flex items-center justify-center">
                          <Loader2 className="w-5 h-5 animate-spin text-ceramic-400" />
                        </div>
                      ) : style.referenceImages[0] ? (
                        <img
                          src={style.referenceImages[0]}
                          alt={style.name}
                          className="h-16 aspect-video object-cover rounded-lg border border-ceramic-200"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-ceramic-100 rounded-lg flex items-center justify-center text-ceramic-400">
                          無
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-ceramic-900">{style.name}</div>
                      <div className="text-sm text-ceramic-500 mt-1 line-clamp-1">
                        {style.description || "無描述"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-ceramic-100 text-ceramic-600 text-sm rounded">
                        {style.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={cn(
                          "px-2 py-1 text-sm rounded",
                          statusLabels[style.status].color
                        )}
                      >
                        {statusLabels[style.status].label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-ceramic-600">{style.usageCount.toLocaleString()}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex gap-1 items-center justify-end">
                        <button
                          onClick={() => regeneratePreview(style)}
                          disabled={isRegenerating || !style.prompt}
                          title="重新生成預覽圖"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className={cn("w-4 h-4", isRegenerating && "animate-spin")} />
                        </button>
                        <Link
                          to={`/styles/${style.id}`}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-ceramic-700 hover:bg-ceramic-50 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleToggle(style.id, style.isEnabled)}
                          disabled={isToggling}
                          title={style.isEnabled ? "隱藏" : "顯示"}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-ceramic-700 hover:bg-ceramic-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isToggling ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : style.isEnabled ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => setPendingDelete(style)}
                          disabled={isDeleting}
                          title="刪除"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isDeleting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-ceramic-200">
            <div className="text-sm text-ceramic-500">
              共 {data.total} 筆，第 {page} / {data.totalPages} 頁
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-ceramic-200 rounded-lg hover:bg-ceramic-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={page === data.totalPages}
                className="p-2 border border-ceramic-200 rounded-lg hover:bg-ceramic-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <ConfirmModal
        open={pendingDelete !== null}
        title="刪除風格"
        description={
          pendingDelete ? (
            <>
              確定要刪除「<strong>{pendingDelete.name}</strong>」嗎？此操作無法復原。
            </>
          ) : null
        }
        confirmLabel="刪除"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {/* Batch regenerate confirmation */}
      <ConfirmModal
        open={confirmBatchRegen}
        title="重新生成預覽圖"
        description={`要重新生成這 ${selectedIds.size} 個模板的預覽圖嗎？原有的預覽圖將被取代。`}
        confirmLabel="開始生成"
        onConfirm={() => {
          setConfirmBatchRegen(false);
          void runBatchRegenerate();
        }}
        onCancel={() => setConfirmBatchRegen(false)}
      />
    </div>
  );
}
